import {
  Building2,
  CalendarDays,
  Castle,
  Church,
  Coffee,
  Droplets,
  FerrisWheel,
  Footprints,
  Landmark,
  MapPin,
  PawPrint,
  Plus,
  RotateCcw,
  Sparkles,
  Waves,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type FC } from 'react';
import { createPortal } from 'react-dom';
import { defineMessages, useIntl } from 'react-intl';

import {
  HOTEL_PLACEHOLDER_IMAGES,
  PLACE_PLACEHOLDER_IMAGES,
  VACATION_PLACEHOLDER_IMAGES,
} from '@/app/agent/media.ts';
import { OpenStreetMapGuideMap, type GuideMapMarker } from './OpenStreetMapGuideMap.tsx';
import type { A2uiNodeViewProps } from '@/app/lib/a2ui/catalog.tsx';
import { arr, num, str } from '@/app/lib/a2ui/props.ts';
import { useSurfaceAction } from '@/app/lib/a2ui/surface-context.ts';
import { trpc } from '@/app/lib/trpc';
import { isRecord } from '@/app/lib/util/type-guards.ts';

/**
 * Client half of `DestinationShortlist`. The first screen is a readable set of
 * matched places; image lookup is deliberately asynchronous and the local guide
 * is an illustrative planning aid, never a live booking result. Incomplete guide
 * arrays request a same-card refresh before the popup exposes guide content.
 */

const messages = defineMessages({
  window: {
    id: 'destinationShortlist.window',
    defaultMessage: 'Best time to fly',
    description: 'Label before the recommended seasonal flight window on a shortlisted destination',
  },
  tip: {
    id: 'destinationShortlist.tip',
    defaultMessage: 'Worth knowing',
    description: 'Label before the honest caveat on a shortlisted destination',
  },
  snapshot: {
    id: 'destinationShortlist.snapshot',
    defaultMessage: 'Planning snapshot',
    description: 'Heading for compact illustrative destination planning signals on a shortlist card',
  },
  cafesNearby: {
    id: 'destinationShortlist.cafesNearby',
    defaultMessage: 'Cafés',
    description: 'Compact label for a broad nearby café-options estimate and example price range',
  },
  thingsToDo: {
    id: 'destinationShortlist.thingsToDo',
    defaultMessage: 'To do',
    description: 'Compact label for the nearby entertainment and activity summary',
  },

  showPlaces: {
    id: 'destinationShortlist.showPlaces',
    defaultMessage: 'Show places',
    description: 'Button that opens the illustrative local place guide',
  },
  closeGuide: {
    id: 'destinationShortlist.closeGuide',
    defaultMessage: 'Close place guide',
    description: 'Accessible label for closing the local place guide dialog',
  },
  guideHeading: {
    id: 'destinationShortlist.guideHeading',
    defaultMessage: 'Around {city}',
    description: 'Heading for the local place guide dialog',
  },

  imagePlaceholder: {
    id: 'destinationShortlist.imagePlaceholder',
    defaultMessage: 'Illustrative travel placeholder',
    description: 'Accessible label for a generic travel placeholder when no grounded place photo is available',
  },
  hotelImagePlaceholder: {
    id: 'destinationShortlist.hotelImagePlaceholder',
    defaultMessage: 'Hotel placeholder image for {name}',
    description: 'Accessible label for a hotel-themed placeholder when a distinct real hotel photo is unavailable',
  },
  imageLoading: {
    id: 'destinationShortlist.imageLoading',
    defaultMessage: 'Loading real place photo',
    description: 'Loading state while a real place photograph is fetched',
  },
  mapLabel: {
    id: 'destinationShortlist.mapLabel',
    defaultMessage: 'Area map · illustrative',
    description: 'Accessible label for the infographic area map',
  },
  selectedOnMap: {
    id: 'destinationShortlist.selectedOnMap',
    defaultMessage: 'Selected on map',
    description: 'Small status chip linking a guide card to the one active map pin',
  },
  topHotels: {
    id: 'destinationShortlist.topHotels',
    defaultMessage: 'Top 5 hotel examples',
    description: 'Heading for the five strongest illustrative hotel examples in the guide',
  },
  nearbyPicks: {
    id: 'destinationShortlist.nearbyPicks',
    defaultMessage: 'Top 5 hotel picks',
    description: 'Heading for the five strongest hotel examples in the local place guide',
  },
  placesToGo: {
    id: 'destinationShortlist.placesToGo',
    defaultMessage: 'Places to go',
    description: 'Heading for varied nearby places worth visiting in the guide',
  },
  hotels: {
    id: 'destinationShortlist.hotels',
    defaultMessage: 'Hotels',
    description: 'Heading for hotel examples in the local place guide',
  },
  cafes: {
    id: 'destinationShortlist.cafes',
    defaultMessage: 'Cafés',
    description: 'Heading for café examples in the local place guide',
  },
  entertainment: {
    id: 'destinationShortlist.entertainment',
    defaultMessage: 'Things to do',
    description: 'Heading for entertainment examples in the local place guide',
  },
  emptyGuide: {
    id: 'destinationShortlist.emptyGuide',
    defaultMessage: 'We have not loaded specific place examples for this destination yet.',
    description: 'Fallback state for a local place guide without named place data',
  },
  loadingGuide: {
    id: 'destinationShortlist.loadingGuide',
    defaultMessage: 'Opening the {city} guide…',
    description: 'Accessible status while the agent adds named places to a local guide',
  },
  guideLoadingHint: {
    id: 'destinationShortlist.guideLoadingHint',
    defaultMessage: 'The shortlist stays open while we add named local picks.',
    description: 'Supporting status while named local guide data is being added',
  },
  guideLoadFailed: {
    id: 'destinationShortlist.guideLoadFailed',
    defaultMessage: 'The guide for {city} did not finish loading. Try again.',
    description: 'Status shown when a local guide request stops before named places arrive',
  },
  retryGuide: {
    id: 'destinationShortlist.retryGuide',
    defaultMessage: 'Try again',
    description: 'Button that requests a local place guide once more after a failed attempt',
  },
  freePrice: {
    id: 'destinationShortlist.freePrice',
    defaultMessage: 'free entry',
    description: 'Compact map pin label for a place that costs nothing to visit',
  },
  plan: {
    id: 'destinationShortlist.plan',
    defaultMessage: 'Plan this trip',
    description: 'Button on a shortlisted destination that starts planning it',
  },
  refine: {
    id: 'destinationShortlist.refine',
    defaultMessage: 'None of these — show me others',
    description: 'Button asking for a different set of shortlisted destinations',
  },
  loadMore: {
    id: 'destinationShortlist.loadMore',
    defaultMessage: 'Load more',
    description: 'Button asking for another batch of destination cards',
  },
  empty: {
    id: 'destinationShortlist.empty',
    defaultMessage:
      'No places on this shortlist yet. Tell us the feeling you are after and roughly when you can travel, and we will fill it.',
    description: 'Empty state when a shortlist arrives with no destinations',
  },
});

type GuideSectionKey = 'hotels' | 'placesToGo' | 'cafes' | 'entertainments';

const GUIDE_SECTION_DEFINITIONS = [
  { key: 'hotels', label: messages.hotels, Icon: Building2, markerClass: 'bg-travel-shortlist-foreground' },
  { key: 'placesToGo', label: messages.placesToGo, Icon: MapPin, markerClass: 'bg-travel-shortlist-alert' },
  { key: 'cafes', label: messages.cafes, Icon: Coffee, markerClass: 'bg-primary' },
  {
    key: 'entertainments',
    label: messages.entertainment,
    Icon: Sparkles,
    markerClass: 'bg-travel-shortlist-foreground',
  },
] as const;

const MAP_MARKER_LIMIT = 25;
// Guide enrichment normally lands in ~20-35s; past this we assume the turn was lost.
const GUIDE_LOAD_TIMEOUT_MS = 75_000;
// Photo lookups for a guide popup are split into small waves so its pictures
// appear as they resolve. Above this many pending photos the request is left
// whole: that is the shortlist arriving at once, which the server already
// parallelises internally.
const IMAGE_LOOKUP_WAVE_SIZE = 2;
const IMAGE_LOOKUP_WAVE_LIMIT = 8;
// How many of the strongest matches get their town coordinates warmed up while
// the visitor is still reading the shortlist, and how long we wait first so the
// warm-up never competes with the photos the screen is already fetching.
const GUIDE_CITY_PREFETCH_COUNT = 5;
const GUIDE_CITY_PREFETCH_DELAY_MS = 2_000;

type SelectedGuideItem = {
  sectionKey: GuideSectionKey;
  name: string;
};

function getGuideMarker(item: GuideItem, section: (typeof GUIDE_SECTION_DEFINITIONS)[number]) {
  const searchable = normalizeImageQuery(`${item.category} ${item.name} ${item.detail}`);
  const markerClass = section.markerClass;
  if (/\b(beaches?|swimming spot|swim|cove|lagoon|coast(?:al)?|shore)\b/.test(searchable)) {
    return { Icon: Waves, markerClass };
  }
  if (/\b(zoo|wildlife|safari)\b/.test(searchable)) {
    return { Icon: PawPrint, markerClass };
  }
  if (/\b(aquapark|aqua park|waterpark|water park|water slide)\b/.test(searchable)) {
    return { Icon: Waves, markerClass };
  }
  if (/\b(temple|shrine|monastery|mosque|church|cathedral)\b/.test(searchable)) {
    return { Icon: Church, markerClass };
  }
  if (/\b(waterfall|cascade|spring)\b/.test(searchable)) {
    return { Icon: Droplets, markerClass };
  }
  if (/\b(castle|fort|palace)\b/.test(searchable)) {
    return { Icon: Castle, markerClass };
  }
  if (/\b(architecture|architectural|building|lighthouse|bridge|ruins?|monument)\b/.test(searchable)) {
    return { Icon: Landmark, markerClass };
  }
  if (/\b(amusement|theme park|funfair)\b/.test(searchable)) {
    return { Icon: FerrisWheel, markerClass };
  }
  if (/\b(viewpoint|lookout|panorama|hike|trail|walk)\b/.test(searchable)) {
    return { Icon: Footprints, markerClass };
  }
  return { Icon: section.Icon, markerClass };
}

type ResolvedPlaceImage = {
  imageUrl: string;
  sourceUrl: string;
  title: string;
  credit: string;
  license: string;
  matchType: 'exact' | 'nearby' | 'regional';
};

type ImageLookupCandidate = {
  key: string;
  query: string;
};

function normalizeImageQuery(query: string): string {
  return query.trim().replace(/\s+/g, ' ').toLowerCase();
}

/**
 * Which of the curated holiday photographs stands in for a card. The choice is
 * a hash of the card's own name, so a given place or hotel always shows the same
 * placeholder — no reshuffling between renders — while neighbouring cards in a
 * list get different scenes.
 */
function hashSeed(seed: string): number {
  let hash = 0;
  for (const character of seed) {
    hash = (hash * 31 + character.charCodeAt(0)) >>> 0;
  }
  return hash;
}

function getPlaceholderImage(seed: string, kind?: 'hotel', index = 0): string {
  const pool = kind === 'hotel' ? HOTEL_PLACEHOLDER_IMAGES : PLACE_PLACEHOLDER_IMAGES;
  const position = (hashSeed(seed) + index) % pool.length;
  return pool[position] ?? VACATION_PLACEHOLDER_IMAGES[0] ?? '';
}

interface GuideItem {
  name: string;
  category: string;
  area: string;
  detail: string;
  priceTag: string;
  imageUrl: string;
  imageAlt: string;
  imageSourceUrl: string;
  imageCredit: string;
  imageLicense: string;
}

interface ShortlistPlace {
  id: string;
  name: string;
  country: string;
  city: string;
  imageUrl: string;
  imageAlt: string;
  imageSourceUrl: string;
  imageCredit: string;
  imageLicense: string;
  pitch: string;
  tags: string[];
  bestWindow: string;
  watchOut: string;
  hotelOptions?: number;
  hotelPriceRange: string;
  cafeOptions?: number;
  cafePriceRange: string;
  nearbyInfrastructure: string;
  thingsToDo: string;
  hotels: GuideItem[];
  placesToGo: GuideItem[];
  cafes: GuideItem[];
  entertainments: GuideItem[];
}

function guideItemsHaveDetails(items: GuideItem[]): boolean {
  return items.length > 0 && items.every((item) => item.detail.trim().length > 0);
}

function hasCompleteGuideDetails(place: ShortlistPlace): boolean {
  return (
    place.hotels.length >= 5 &&
    guideItemsHaveDetails(place.hotels) &&
    place.placesToGo.length >= 3 &&
    guideItemsHaveDetails(place.placesToGo) &&
    place.cafes.length >= 3 &&
    guideItemsHaveDetails(place.cafes) &&
    place.entertainments.length >= 3 &&
    guideItemsHaveDetails(place.entertainments)
  );
}


function hasCompactShortlistDetails(place: ShortlistPlace): boolean {
  return (
    place.pitch.trim().length > 0 &&
    place.tags.length >= 2 &&
    place.bestWindow.trim().length > 0 &&
    place.watchOut.trim().length > 0 &&
    place.cafePriceRange.trim().length > 0 &&
    place.thingsToDo.trim().length > 0
  );
}

function readGuideItems(value: unknown): GuideItem[] {
  const items: GuideItem[] = [];
  for (const entry of arr(value)) {
    if (!isRecord(entry)) {
      continue;
    }
    const name = str(entry.name);
    if (name.length === 0) {
      continue;
    }
    items.push({
      name,
      category: str(entry.category),
      area: str(entry.area),
      detail: str(entry.detail) || str(entry.description),
      priceTag: str(entry.priceTag),
      imageUrl: str(entry.imageUrl),
      imageAlt: str(entry.imageAlt),
      imageSourceUrl: str(entry.imageSourceUrl),
      imageCredit: str(entry.imageCredit),
      imageLicense: str(entry.imageLicense),
    });
  }
  return items;
}

function mergeGuideItems(...lists: GuideItem[][]): GuideItem[] {
  const seen = new Set<string>();
  const merged: GuideItem[] = [];
  for (const item of lists.flat()) {
    const key = normalizeImageQuery(item.name);
    if (!key || seen.has(key)) {
      continue;
    }
    seen.add(key);
    merged.push(item);
  }
  return merged;
}

function displayGuidePrice(priceTag: string): string {
  return priceTag.replace(/\s+(?:illustrative|example)\b/gi, '').trim();
}

const FREE_PRICE_PATTERN = /\bfree\b/i;
const MONEY_PATTERN =
  /(?:US\$|[€$£])\s?\d[\d.,]*(?:\s*[–—-]\s*(?:US\$|[€$£])?\s?\d[\d.,]*)?|\d[\d.,]*(?:\s*[–—-]\s*\d[\d.,]*)?\s*(?:€|\$|£|EUR|USD|GBP)/i;

/**
 * Map pins carry a one-line label, so the verbose planning wording
 * ("€25–45 per person example, not live") is reduced to its money and unit:
 * "€25-45/person", or the free-entry label when there is no price at all.
 */
function compactGuidePrice(priceTag: string, freeLabel: string): string {
  const cleaned = displayGuidePrice(priceTag)
    .replace(/\bnot live\b/gi, '')
    .replace(/[\s,;:·]+$/g, '')
    .trim();
  const money = cleaned.match(MONEY_PATTERN)?.[0] ?? '';
  if (!money) {
    return FREE_PRICE_PATTERN.test(cleaned) ? freeLabel : cleaned;
  }
  const unit = /per person|\/\s*person|\bpp\b/i.test(cleaned)
    ? '/person'
    : /per night|\/\s*night|\bnightly\b/i.test(cleaned)
      ? '/night'
      : /per day|\/\s*day/i.test(cleaned)
        ? '/day'
        : '';
  return `${money.replace(/\s*[–—-]\s*/, '-').replace(/\s+/g, '')}${unit}`;
}

function readPlaces(value: unknown): ShortlistPlace[] {
  const places: ShortlistPlace[] = [];
  const seenIds = new Set<string>();
  for (const entry of arr(value)) {
    if (!isRecord(entry)) {
      continue;
    }
    const name = str(entry.name);
    if (name.length === 0) {
      continue;
    }
    const id = str(entry.id) || name;
    if (seenIds.has(id)) {
      continue;
    }
    seenIds.add(id);
    places.push({
      id,
      name,
      country: str(entry.country),
      city: str(entry.city),
      imageUrl: str(entry.imageUrl),
      imageAlt: str(entry.imageAlt),
      imageSourceUrl: str(entry.imageSourceUrl),
      imageCredit: str(entry.imageCredit),
      imageLicense: str(entry.imageLicense),
      pitch: str(entry.pitch),
      tags: arr(entry.tags)
        .map(str)
        .filter((tag) => tag.length > 0),
      bestWindow: str(entry.bestWindow),
      watchOut: str(entry.watchOut),
      hotelOptions: num(entry.hotelOptions),
      hotelPriceRange: str(entry.hotelPriceRange),
      cafeOptions: num(entry.cafeOptions),
      cafePriceRange: str(entry.cafePriceRange),
      nearbyInfrastructure: str(entry.nearbyInfrastructure),
      thingsToDo: str(entry.thingsToDo),
      hotels: readGuideItems(entry.hotels),
      placesToGo: mergeGuideItems(readGuideItems(entry.placesToGo), readGuideItems(entry.beaches)),
      cafes: readGuideItems(entry.cafes),
      entertainments: readGuideItems(entry.entertainments),
    });
  }
  return places;
}

const SHORTLIST_CACHE_KEY = 'wayfarer.destination-shortlist.v1';

/**
 * The shortlist survives a surface remount two ways. The in-page copy is the
 * reliable one: a one-card guide patch arrives as a fresh surface, so the new
 * instance would otherwise mount holding that single card and the other
 * nineteen places would vanish from the screen. Session storage additionally
 * carries the list across a page reload, and is best-effort — some embeddings
 * refuse it.
 */
/**
 * The shortlist survives a surface remount three ways. The in-page copy is the
 * fastest: a one-card guide patch arrives as its own render, so a remounted
 * instance would otherwise hold that single card and the other nineteen places
 * would vanish. Session and local storage carry the list across a page reload
 * or a second tab, where the transcript replays only that last one-card patch.
 * Storage is best-effort — some embeddings refuse it.
 */
let shortlistMemoryCache: ShortlistPlace[] = [];

/**
 * Photos already resolved for this page. A remount (a guide patch arrives as a
 * fresh surface) would otherwise re-run all twenty image lookups and leave the
 * cards grey while they come back a second time.
 */
const shortlistImageMemory = new Map<string, ResolvedPlaceImage[] | null>();

function readStoredShortlist(store: Storage | undefined): ShortlistPlace[] {
  try {
    const cached = store?.getItem(SHORTLIST_CACHE_KEY);
    const places = cached ? readPlaces(JSON.parse(cached)) : [];
    return places.length >= 20 && places.every(hasCompactShortlistDetails) ? places : [];
  } catch {
    return [];
  }
}

function readCachedShortlist(): ShortlistPlace[] {
  const candidates = [shortlistMemoryCache];
  if (typeof window !== 'undefined') {
    candidates.push(readStoredShortlist(window.sessionStorage), readStoredShortlist(window.localStorage));
  }
  return candidates.reduce<ShortlistPlace[]>(
    (longest, candidate) => (candidate.length > longest.length ? candidate : longest),
    [],
  );
}

function writeCachedShortlist(places: ShortlistPlace[]): void {
  if (places.length < 20) {
    return;
  }
  shortlistMemoryCache = places;
  if (typeof window === 'undefined') {
    return;
  }
  const serialized = JSON.stringify(places);
  for (const store of [window.sessionStorage, window.localStorage]) {
    try {
      store?.setItem(SHORTLIST_CACHE_KEY, serialized);
    } catch {
      // Storage is an optimization for surface remounts, not a requirement.
    }
  }
}

const RemotePlaceImage: FC<{
  imageUrl: string;
  imageAlt: string;
  placeholderKind?: 'hotel';
  placeholderIndex?: number;
  className: string;
  loading?: 'eager' | 'lazy';
  pending?: boolean;
  placeholderSeed?: string;
  onImageError?: () => void;
}> = ({
  imageUrl,
  imageAlt,
  placeholderKind,
  placeholderIndex = 0,
  className,
  loading = 'lazy',
  pending = false,
  placeholderSeed = imageAlt,
  onImageError,
}) => {
  const intl = useIntl();
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [placeholderImageFailed, setPlaceholderImageFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
    setLoaded(false);
    setPlaceholderImageFailed(false);
  }, [imageUrl, pending, placeholderSeed]);

  const placeholderUrl = getPlaceholderImage(placeholderSeed || imageAlt, placeholderKind, placeholderIndex);
  const showRealImage = Boolean(imageUrl) && !failed;
  const placeholderLabel =
    placeholderKind === 'hotel'
      ? intl.formatMessage(messages.hotelImagePlaceholder, { name: imageAlt || 'hotel' })
      : intl.formatMessage(messages.imagePlaceholder);

  // The curated holiday photograph sits underneath every card and is painted at
  // once, so a card is never an empty box: the real photograph, when one
  // arrives, simply fades in on top of it.
  return (
    <div
      className={`${className} group relative overflow-hidden bg-travel-shortlist-background`}
      {...(showRealImage && loaded
        ? {}
        : {
            role: 'img',
            'aria-label': pending && !showRealImage ? intl.formatMessage(messages.imageLoading) : placeholderLabel,
          })}
    >
      {!placeholderImageFailed ? (
        <img
          src={placeholderUrl}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover"
          loading="eager"
          decoding="async"
          onError={() => setPlaceholderImageFailed(true)}
        />
      ) : null}
      {showRealImage ? (
        <img
          src={imageUrl}
          alt={imageAlt || 'Real place photograph'}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-200 motion-reduce:transition-none ${loaded ? 'opacity-100' : 'opacity-0'}`}
          loading={loading}
          decoding="async"
          onLoad={() => setLoaded(true)}
          onError={() => {
            setFailed(true);
            onImageError?.();
          }}
        />
      ) : null}
    </div>
  );
};

export const DestinationShortlist: FC<A2uiNodeViewProps> = ({ node }) => {
  const intl = useIntl();
  const dispatch = useSurfaceAction();
  const [openPlaceId, setOpenPlaceId] = useState<string | null>(null);
  const [loadingGuideId, setLoadingGuideId] = useState<string | null>(null);
  const [failedGuideId, setFailedGuideId] = useState<string | null>(null);
  const [selectedGuideItem, setSelectedGuideItem] = useState<SelectedGuideItem | null>(null);
  // A one-card guide patch re-renders this surface with only that card's props,
  // so the heading is remembered: the screen keeps the shortlist's own title and
  // intro instead of losing them the moment a visitor opens a place guide.
  const headingRef = useRef({ title: '', intro: '' });
  const title = str(node.props.title) || headingRef.current.title;
  const intro = str(node.props.intro) || headingRef.current.intro;
  headingRef.current = { title, intro };
  const incomingPlaces = readPlaces(node.props.destinations);
  const incomingSignature = JSON.stringify(incomingPlaces);
  const [places, setPlaces] = useState<ShortlistPlace[]>(() => {
    if (incomingPlaces.length >= 20 && incomingPlaces.every(hasCompactShortlistDetails)) {
      return incomingPlaces;
    }
    const cached = readCachedShortlist();
    const initialPlaces = cached.length >= 20 ? cached : incomingPlaces.filter(hasCompactShortlistDetails);
    console.log(
      '[DestinationShortlist] Shortlist mount',
      JSON.stringify({ incoming: incomingPlaces.length, cached: cached.length, initial: initialPlaces.length }),
    );
    return initialPlaces;
  });
  const [resolvedImages, setResolvedImages] = useState<Record<string, ResolvedPlaceImage[] | null>>(
    () => Object.fromEntries(shortlistImageMemory),
  );
  const appendNextResponseRef = useRef(false);
  const guideRefreshResponseRef = useRef<string | null>(null);
  const requestedFallbackQueriesRef = useRef(new Set<string>());
  const inFlightImageQueriesRef = useRef(new Set<string>());
  const prefetchedCitiesRef = useRef(new Set<string>());
  const rememberImages = useCallback(
    (entries: Record<string, ResolvedPlaceImage[] | null>): void => {
      for (const [key, value] of Object.entries(entries)) {
        shortlistImageMemory.set(key, value);
      }
      setResolvedImages((current) => ({ ...current, ...entries }));
    },
    [],
  );
  // One guide request may be in flight at a time: a second `loadGuide` dispatch
  // aborts the running turn server-side, which used to strand the popup on its
  // loading line forever. Extra taps are collapsed or queued here instead.
  const loadingGuideIdRef = useRef<string | null>(null);
  const queuedGuideRequestRef = useRef<ShortlistPlace | null>(null);

  useEffect(() => {
    const requestedGuideId = guideRefreshResponseRef.current;
    guideRefreshResponseRef.current = null;
    const shouldAppend = appendNextResponseRef.current;
    appendNextResponseRef.current = false;
    setPlaces((currentPlaces) => {
      const currentById = new Map(currentPlaces.map((place) => [place.id, place]));
      const mergeKnownGuide = (incoming: ShortlistPlace, current: ShortlistPlace | undefined): ShortlistPlace => {
        if (!current) {
          return incoming;
        }
        // Guide responses intentionally contain only the selected card's guide
        // arrays. Their compact shortlist fields are absent (read as empty
        // strings by readPlaces), so spreading the response over the current
        // card would erase its pitch, tags, planning row and photograph.
        const keepText = (incomingValue: string, currentValue: string): string =>
          incomingValue.trim().length > 0 ? incomingValue : currentValue;
        const keepTags = incoming.tags.length > 0 ? incoming.tags : current.tags;
        const keepOptionalNumber = (incomingValue: number | undefined, currentValue: number | undefined) =>
          incomingValue ?? currentValue;
        // An incoming guide array is adopted only when it is at least as full as
        // the one already on screen, so a re-render can never shorten a guide a
        // visitor is reading.
        const keepFuller = (incomingItems: GuideItem[], currentItems: GuideItem[]): GuideItem[] =>
          guideItemsHaveDetails(incomingItems) && incomingItems.length >= currentItems.length
            ? incomingItems
            : currentItems;
        return {
          ...current,
          id: keepText(incoming.id, current.id),
          name: keepText(incoming.name, current.name),
          country: keepText(incoming.country, current.country),
          city: keepText(incoming.city, current.city),
          imageUrl: keepText(incoming.imageUrl, current.imageUrl),
          imageAlt: keepText(incoming.imageAlt, current.imageAlt),
          imageSourceUrl: keepText(incoming.imageSourceUrl, current.imageSourceUrl),
          imageCredit: keepText(incoming.imageCredit, current.imageCredit),
          imageLicense: keepText(incoming.imageLicense, current.imageLicense),
          pitch: keepText(incoming.pitch, current.pitch),
          tags: keepTags,
          bestWindow: keepText(incoming.bestWindow, current.bestWindow),
          watchOut: keepText(incoming.watchOut, current.watchOut),
          hotelOptions: keepOptionalNumber(incoming.hotelOptions, current.hotelOptions),
          hotelPriceRange: keepText(incoming.hotelPriceRange, current.hotelPriceRange),
          cafeOptions: keepOptionalNumber(incoming.cafeOptions, current.cafeOptions),
          cafePriceRange: keepText(incoming.cafePriceRange, current.cafePriceRange),
          nearbyInfrastructure: keepText(incoming.nearbyInfrastructure, current.nearbyInfrastructure),
          thingsToDo: keepText(incoming.thingsToDo, current.thingsToDo),
          hotels: keepFuller(incoming.hotels, current.hotels),
          placesToGo: keepFuller(incoming.placesToGo, current.placesToGo),
          cafes: keepFuller(incoming.cafes, current.cafes),
          entertainments: keepFuller(incoming.entertainments, current.entertainments),
        };
      };
      const guideRefreshId =
        requestedGuideId ??
        // A one-card payload is a guide patch only when a full shortlist is
        // already on screen. While the first shortlist streams in, early
        // frames are simply the shortlist itself, still growing.
        (incomingPlaces.length === 1 && currentPlaces.length >= 20
          ? incomingPlaces[0]?.id ?? null
          : null) ??
        (openPlaceId &&
        currentPlaces.length >= 20 &&
        incomingPlaces.length < currentPlaces.length &&
        incomingPlaces.some((place) => place.id === openPlaceId)
          ? openPlaceId
          : null);
      if (guideRefreshId) {
        const incomingById = new Map(incomingPlaces.map((place) => [place.id, place]));
        const merged = currentPlaces.map((currentPlace) => {
          const refreshed = incomingById.get(currentPlace.id);
          return refreshed && refreshed.id === guideRefreshId
            ? mergeKnownGuide(refreshed, currentPlace)
            : currentPlace;
        });
        console.log(
          '[DestinationShortlist] Guide patch merged',
          JSON.stringify({
            destinationId: guideRefreshId,
            incoming: incomingPlaces.length,
            kept: merged.length,
          }),
        );
        return merged;
      }
      if (!shouldAppend) {
        if (incomingPlaces.length < currentPlaces.length) {
          // A guide patch, replay frame or interrupted refresh can contain fewer
          // cards than the shortlist already on screen. Never let that payload
          // replace the list; merge matching cards and keep every other card.
          const incomingById = new Map(incomingPlaces.map((place) => [place.id, place]));
          const currentIds = new Set(currentPlaces.map((place) => place.id));
          const merged = currentPlaces.map((currentPlace) => {
            const incoming = incomingById.get(currentPlace.id);
            return incoming ? mergeKnownGuide(incoming, currentPlace) : currentPlace;
          });
          const additions = incomingPlaces.filter(
            (place) => !currentIds.has(place.id) && hasCompactShortlistDetails(place),
          );
          console.log(
            '[DestinationShortlist] Shorter payload merged without loss',
            JSON.stringify({ incoming: incomingPlaces.length, kept: merged.length, additions: additions.length }),
          );
          return [...merged, ...additions];
        }
        const compactIncomingPlaces = incomingPlaces.filter(hasCompactShortlistDetails);
        if (compactIncomingPlaces.length < incomingPlaces.length) {
          const compactById = new Map(compactIncomingPlaces.map((place) => [place.id, place]));
          const currentIds = new Set(currentPlaces.map((place) => place.id));
          const merged = currentPlaces.map((currentPlace) =>
            mergeKnownGuide(compactById.get(currentPlace.id) ?? currentPlace, currentPlace),
          );
          const additions = compactIncomingPlaces
            .filter((place) => !currentIds.has(place.id))
            .map((place) => mergeKnownGuide(place, currentById.get(place.id)));
          return [...merged, ...additions];
        }
        return incomingPlaces.map((place) => mergeKnownGuide(place, currentById.get(place.id)));
      }
      const knownIds = new Set(currentPlaces.map((place) => place.id));
      return [
        ...currentPlaces,
        ...incomingPlaces
          .filter((place) => !knownIds.has(place.id) && hasCompactShortlistDetails(place))
          .map((place) => mergeKnownGuide(place, currentById.get(place.id))),
      ];
    });
  }, [incomingSignature]);

  useEffect(() => {
    writeCachedShortlist(places);
  }, [places]);

  useEffect(() => {
    console.log(
      '[DestinationShortlist] Shortlist painted',
      JSON.stringify({
        count: places.length,
        firstId: places[0]?.id ?? '',
        firstPitch: Boolean(places[0]?.pitch),
        firstTags: places[0]?.tags.length ?? 0,
        firstWindow: Boolean(places[0]?.bestWindow),
        firstGuideComplete: places[0] ? hasCompleteGuideDetails(places[0]) : false,
      }),
    );
  }, [places]);

  // Warm the town coordinates for the strongest matches while the visitor is
  // still reading the shortlist. The guide map needs its city centre before it
  // can frame anything, and that lookup is cached on the server for a week — so
  // paying for it here, quietly and one at a time, means the map in the first
  // popup a visitor opens is framed the moment it appears.
  useEffect(() => {
    if (openPlaceId) {
      return;
    }
    const candidates = places
      .slice(0, GUIDE_CITY_PREFETCH_COUNT)
      .map((place) => ({ city: place.city || place.name, country: place.country }))
      .filter((candidate) => candidate.city.length > 0);
    if (candidates.length === 0) {
      return;
    }
    let cancelled = false;
    const timeoutId = window.setTimeout(async () => {
      for (const candidate of candidates) {
        const cacheKey = `${candidate.city}|${candidate.country}`.toLowerCase();
        if (cancelled || prefetchedCitiesRef.current.has(cacheKey)) {
          continue;
        }
        prefetchedCitiesRef.current.add(cacheKey);
        try {
          await trpc.placeGeocoding.search.query({
            city: candidate.city,
            country: candidate.country,
            places: [],
          });
        } catch {
          // A warm-up miss costs nothing: the popup will look the city up itself.
          prefetchedCitiesRef.current.delete(cacheKey);
        }
      }
    }, GUIDE_CITY_PREFETCH_DELAY_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [places, openPlaceId]);

  const sendGuideRequest = useCallback(
    (place: ShortlistPlace) => {
      queuedGuideRequestRef.current = null;
      loadingGuideIdRef.current = place.id;
      guideRefreshResponseRef.current = place.id;
      setFailedGuideId(null);
      setLoadingGuideId(place.id);
      dispatch?.('loadGuide', {
        destinationId: place.id,
        name: place.name,
        city: place.city || place.name,
        country: place.country,
        visibleDestinationIds: places.map((visiblePlace) => visiblePlace.id),
      });
    },
    [dispatch, places],
  );

  const requestGuide = useCallback(
    (place: ShortlistPlace) => {
      if (hasCompleteGuideDetails(place)) {
        return;
      }
      if (loadingGuideIdRef.current === place.id) {
        // Re-tapping the same card would abort the turn that is already fetching it.
        return;
      }
      if (loadingGuideIdRef.current) {
        queuedGuideRequestRef.current = place;
        return;
      }
      sendGuideRequest(place);
    },
    [sendGuideRequest],
  );

  const finishGuideRequest = useCallback(
    (failedId: string | null) => {
      loadingGuideIdRef.current = null;
      setLoadingGuideId(null);
      setFailedGuideId(failedId);
      const queued = queuedGuideRequestRef.current;
      queuedGuideRequestRef.current = null;
      if (!queued) {
        return;
      }
      const latest = places.find((place) => place.id === queued.id) ?? queued;
      if (!hasCompleteGuideDetails(latest)) {
        sendGuideRequest(latest);
      }
    },
    [places, sendGuideRequest],
  );

  const finishGuideRequestRef = useRef(finishGuideRequest);
  useEffect(() => {
    finishGuideRequestRef.current = finishGuideRequest;
  }, [finishGuideRequest]);

  useEffect(() => {
    if (!loadingGuideId) {
      return;
    }
    const refreshedPlace = places.find((place) => place.id === loadingGuideId);
    if (refreshedPlace && hasCompleteGuideDetails(refreshedPlace)) {
      finishGuideRequestRef.current(null);
    }
  }, [loadingGuideId, places]);

  // Safety net: if the enrichment turn never lands (aborted, dropped or slow),
  // surface a retry instead of an endless loading line.
  useEffect(() => {
    if (!loadingGuideId) {
      return;
    }
    const timeoutId = window.setTimeout(() => {
      finishGuideRequestRef.current(loadingGuideId);
    }, GUIDE_LOAD_TIMEOUT_MS);
    return () => window.clearTimeout(timeoutId);
  }, [loadingGuideId]);

  const selectedPlace = openPlaceId ? places.find((place) => place.id === openPlaceId) : undefined;
  const selectedCity = selectedPlace?.city || selectedPlace?.name || '';
  const selectedSections = selectedPlace
    ? GUIDE_SECTION_DEFINITIONS.map((section) => ({
        ...section,
        items: selectedPlace[section.key],
      }))
    : [];
  const hotelSection = selectedSections.find((section) => section.key === 'hotels');
  const hotelOffers = hotelSection?.items.slice(0, 5) ?? [];
  const nearbySections = selectedSections.filter((section) => section.key !== 'hotels');
  const mapItems = [
    ...(hotelSection?.items.slice(0, 5).map((item, index) => ({
      ...hotelSection,
      item,
      rank: index + 1,
    })) ?? []),
    ...nearbySections.flatMap((section) =>
      section.items.map((item) => ({
        ...section,
        item,
        rank: undefined,
      })),
    ),
  ]
    .filter(
      (marker, index, markers) =>
        markers.findIndex(
          (candidate) => normalizeImageQuery(candidate.item.name) === normalizeImageQuery(marker.item.name),
        ) === index,
    )
    .slice(0, MAP_MARKER_LIMIT);
  const mapMarkers: GuideMapMarker[] = mapItems.map((marker) => ({
    // Section keys repeat for every hotel/place/café. The map needs an identity
    // for the individual item, otherwise one click selects/geocodes the whole
    // colour group and every item lands on the same coordinate.
    key: `${marker.key}:${normalizeImageQuery(marker.item.name)}`,
    sectionKey: marker.key,
    name: marker.item.name,
    category: marker.item.category,
    area: marker.item.area,
    markerClass: marker.markerClass,
    priceTag: marker.item.priceTag
      ? compactGuidePrice(marker.item.priceTag, intl.formatMessage(messages.freePrice))
      : undefined,
    rank: marker.rank,
    selected:
      selectedGuideItem?.sectionKey === marker.key &&
      normalizeImageQuery(selectedGuideItem.name) === normalizeImageQuery(marker.item.name),
  }));
  const topFivePicks = selectedPlace
    ? hotelOffers.map((item) => ({
        item,
        Icon: Building2,
        markerClass: 'bg-travel-shortlist-foreground',
      }))
    : [];
  const hasGuideItems = selectedPlace ? hasCompleteGuideDetails(selectedPlace) : false;
  const isSelectedGuideLoading = Boolean(selectedPlace) && loadingGuideId === selectedPlace?.id;
  const renderNearbyGuideCards = () => {
    if (!nearbySections.some((section) => section.items.length > 0)) {
      return null;
    }
    return (
      <div className="flex min-w-0 flex-col gap-4">
        {nearbySections.map((section) => {
          const selectedItemName =
            selectedGuideItem?.sectionKey === section.key ? selectedGuideItem.name : '';
          const item =
            section.items.find(
              (candidate) => normalizeImageQuery(candidate.name) === normalizeImageQuery(selectedItemName),
            ) ?? section.items[0];
          if (!item) {
            return null;
          }
          const isSelectedOnMap =
            selectedGuideItem?.sectionKey === section.key &&
            normalizeImageQuery(selectedGuideItem.name) === normalizeImageQuery(item.name);
          const SectionIcon = section.Icon;
          return (
            <div
              key={`nearby-${section.key}`}
              className={`flex min-w-0 items-start gap-3 rounded-[15px] border bg-travel-shortlist-background p-3.5 transition-[border-color,box-shadow] duration-150 motion-reduce:transition-none ${
                selectedGuideItem?.sectionKey === section.key &&
                normalizeImageQuery(selectedGuideItem.name) === normalizeImageQuery(item.name)
                  ? 'border-primary ring-2 ring-primary/20'
                  : 'border-travel-shortlist-border'
              }`}
            >
              <span
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-travel-shortlist-card ${section.markerClass}`}
              >
                <SectionIcon className="h-4 w-4" aria-hidden="true" />
              </span>
              <div className="flex min-w-0 flex-1 flex-col">
                <div className="flex min-w-0 flex-col gap-1">
                  <p className="break-words text-sm font-bold tracking-[0.3px] text-travel-shortlist-foreground">
                    {intl.formatMessage(section.label)}
                  </p>
                  <div className="flex min-w-0 flex-wrap items-center gap-2">
                    <p className="min-w-0 break-words text-sm font-bold text-travel-shortlist-muted">
                      {item.name}
                    </p>
                    {isSelectedOnMap ? (
                      <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em] text-primary">
                        {intl.formatMessage(messages.selectedOnMap)}
                      </span>
                    ) : null}
                  </div>
                </div>
                {item.detail ? (
                  <p className="mt-2 whitespace-normal break-words text-xs leading-relaxed text-travel-shortlist-muted">
                    {item.detail}
                  </p>
                ) : null}
                {item.priceTag ? (
                  <span className="mt-2 inline-flex max-w-full self-start whitespace-normal break-words rounded-full border border-travel-shortlist-border bg-travel-shortlist-card px-2.5 py-0.5 text-xs font-semibold leading-snug text-travel-shortlist-foreground">
                    {displayGuidePrice(item.priceTag)}
                  </span>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderGuideLoadingBanner = () => (
    <div
      className="rounded-[15px] border border-travel-shortlist-border bg-travel-shortlist-background p-4"
      aria-busy="true"
      aria-live="polite"
    >
      <div className="flex min-w-0 items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-travel-shortlist-card">
          <MapPin className="h-4 w-4" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-bold text-travel-shortlist-foreground">
            {intl.formatMessage(messages.loadingGuide, { city: selectedCity })}
          </p>
          <p className="mt-1 text-sm leading-relaxed text-travel-shortlist-muted">
            {intl.formatMessage(messages.guideLoadingHint)}
          </p>
        </div>
      </div>
      {selectedPlace?.pitch ? (
        <p className="mt-4 text-sm leading-relaxed text-travel-shortlist-foreground">{selectedPlace.pitch}</p>
      ) : null}
      {selectedPlace?.thingsToDo ? (
        <p className="mt-3 flex items-start gap-2 text-sm leading-relaxed text-travel-shortlist-muted">
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
          <span>{selectedPlace.thingsToDo}</span>
        </p>
      ) : null}
    </div>
  );

  const renderGuideSectionSkeletons = () => (
    <div className="flex min-w-0 flex-col gap-4" aria-hidden="true">
      {GUIDE_SECTION_DEFINITIONS.slice(1).map((section) => {
        const SectionIcon = section.Icon;
        return (
          <div
            key={`guide-loading-${section.key}`}
            className="flex min-w-0 items-start gap-3 rounded-[15px] border border-travel-shortlist-border bg-travel-shortlist-background p-3.5"
          >
            <span
              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-travel-shortlist-card ${section.markerClass}`}
            >
              <SectionIcon className="h-4 w-4" aria-hidden="true" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold text-travel-shortlist-foreground">
                {intl.formatMessage(section.label)}
              </p>
              <div className="mt-3 h-2 w-4/5 rounded-full bg-travel-shortlist-border/70" />
              <div className="mt-2 h-2 w-3/5 rounded-full bg-travel-shortlist-border/50" />
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderGuidePickSkeletons = () => (
    <section aria-hidden="true">
      <h3 className="text-lg font-bold tracking-[0.3px] text-travel-shortlist-foreground">
        {intl.formatMessage(messages.nearbyPicks)}
      </h3>
      <div className="mt-4 grid items-stretch gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {[0, 1, 2, 3, 4].map((index) => (
          <div
            key={`guide-loading-pick-${index}`}
            className="min-h-44 rounded-[15px] border border-travel-shortlist-border bg-travel-shortlist-card p-3"
          >
            <div className="aspect-[4/3] rounded-[15px] bg-travel-shortlist-border/50" />
            <div className="mt-3 h-3 w-4/5 rounded-full bg-travel-shortlist-border/70" />
            <div className="mt-2 h-2 w-3/5 rounded-full bg-travel-shortlist-border/50" />
          </div>
        ))}
      </div>
    </section>
  );

  useEffect(() => {
    setSelectedGuideItem(null);
  }, [selectedPlace?.id]);

  useEffect(() => {
    if (!selectedPlace) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setOpenPlaceId(null);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [selectedPlace]);

  const imageLookupCandidates: ImageLookupCandidate[] = [];
  const addImageLookup = (query: string): void => {
    const normalizedQuery = normalizeImageQuery(query);
    if (!normalizedQuery || Object.prototype.hasOwnProperty.call(resolvedImages, normalizedQuery)) {
      return;
    }
    if (!imageLookupCandidates.some((candidate) => candidate.key === normalizedQuery)) {
      imageLookupCandidates.push({ key: normalizedQuery, query: query.trim() });
    }
  };

  for (const place of places) {
    if (!place.imageUrl) {
      addImageLookup(`${place.name} ${place.city} ${place.country}`);
    }
  }
  if (selectedPlace) {
    for (const section of selectedSections) {
      for (const item of section.items) {
        if (section.key === 'hotels' || !item.imageUrl) {
          const imageKind = section.key === 'hotels' ? 'hotel exterior' : '';
          addImageLookup(`${item.name} ${item.area} ${imageKind} ${selectedPlace.name} ${selectedPlace.country}`);
        }
      }
    }
  }

  const imageLookupSignature = imageLookupCandidates
    .map((candidate) => `${candidate.key}:${candidate.query}`)
    .join('|');

  useEffect(() => {
    // The shortlist streams in: this effect re-runs every time new cards land.
    // Only the photos nobody has asked for yet are fetched, so a growing list
    // never re-queries the images already in flight for the earlier cards.
    const pendingCandidates = imageLookupCandidates.filter(
      (candidate) => !inFlightImageQueriesRef.current.has(candidate.key),
    );
    if (pendingCandidates.length === 0) {
      return;
    }
    for (const candidate of pendingCandidates) {
      inFlightImageQueriesRef.current.add(candidate.key);
    }
    let cancelled = false;
    // A guide popup asks for a handful of photos at once. Sent as one request
    // they all appear together, gated by the slowest lookup; sent in small
    // groups the hotel pictures fill in as they resolve. A large batch — the
    // whole shortlist arriving at once from cache — stays a single request,
    // because the server already fans that out and splitting it would put far
    // more parallel load on the image service.
    const chunkSize =
      pendingCandidates.length <= IMAGE_LOOKUP_WAVE_LIMIT ? IMAGE_LOOKUP_WAVE_SIZE : pendingCandidates.length;
    const waves: ImageLookupCandidate[][] = [];
    for (let index = 0; index < pendingCandidates.length; index += chunkSize) {
      waves.push(pendingCandidates.slice(index, index + chunkSize));
    }
    for (const wave of waves) {
      trpc.placeImages.search
        .query({ queries: wave.map((candidate) => candidate.query) })
        .then((response) => {
          if (cancelled) {
            return;
          }
          const nextResults: Record<string, ResolvedPlaceImage[] | null> = {};
          const resultByQuery = new Map(
            response.results.map((result) => {
              const images = result.images.map((image) => ({
                ...image,
                matchType: result.matchType ?? image.matchType,
              }));
              return [normalizeImageQuery(result.query), images.length > 0 ? images : null] as const;
            }),
          );
          for (const candidate of wave) {
            nextResults[candidate.key] = resultByQuery.get(candidate.key) ?? null;
          }
          console.log(
            '[DestinationShortlist] Image hydration response',
            JSON.stringify({
              requested: wave.length,
              matched: Object.values(nextResults).filter((image) => image !== null).length,
              nearby: Object.values(nextResults).filter((images) => images?.some((image) => image.matchType === 'nearby')).length,
              regional: Object.values(nextResults).filter((images) => images?.some((image) => image.matchType === 'regional')).length,
            }),
          );
          rememberImages(nextResults);
        })
        .catch((error) => {
          if (cancelled) {
            return;
          }
          console.warn('[DestinationShortlist] Image hydration failed', {
            message: error instanceof Error ? error.message : 'unknown error',
            requested: wave.length,
          });
          const failedResults: Record<string, null> = {};
          for (const candidate of wave) {
            failedResults[candidate.key] = null;
          }
          rememberImages(failedResults);
        });
    }
    return () => {
      // A dropped run must release its keys, otherwise those photos would stay
      // marked in flight and never be requested again.
      cancelled = true;
      for (const candidate of pendingCandidates) {
        inFlightImageQueriesRef.current.delete(candidate.key);
      }
    };
  }, [imageLookupSignature]);

  const getResolvedImages = (query: string): ResolvedPlaceImage[] | null | undefined =>
    resolvedImages[normalizeImageQuery(query)];

  const requestFallbackImage = (query: string): void => {
    const fallbackKey = normalizeImageQuery(query);
    if (requestedFallbackQueriesRef.current.has(fallbackKey)) {
      return;
    }
    requestedFallbackQueriesRef.current.add(fallbackKey);
    trpc.placeImages.search
      .query({ queries: [query], fallbackOnly: true })
      .then((response) => {
        const result = response.results[0];
        const image = result?.images[0];
        console.log(
          '[DestinationShortlist] Browser image fallback response',
          JSON.stringify({
            matchType: result?.matchType ?? 'unavailable',
            matched: Boolean(image),
          }),
        );
        rememberImages({
          [fallbackKey]: result?.images?.length
            ? result.images.map((fallbackImage) => ({
                ...fallbackImage,
                matchType: result.matchType ?? fallbackImage.matchType,
              }))
            : null,
        });
      })
      .catch((error) => {
        console.warn('[DestinationShortlist] Browser image fallback failed', {
          message: error instanceof Error ? error.message : 'unknown error',
        });
        rememberImages({ [fallbackKey]: null });
      });
  };

  const hotelImageSelections = new Map<string, ResolvedPlaceImage | null>();
  const usedHotelImageIds = new Set<string>();
  hotelOffers.forEach((item, index) => {
    if (!selectedPlace) {
      return;
    }
    const itemQuery = `${item.name} ${item.area} hotel exterior ${selectedPlace.name} ${selectedPlace.country}`;
    const suppliedImage = item.imageUrl
      ? {
          imageUrl: item.imageUrl,
          sourceUrl: item.imageSourceUrl,
          title: item.imageAlt || item.name,
          credit: item.imageCredit,
          license: item.imageLicense,
          matchType: 'exact' as const,
        }
      : null;
    const candidates = [suppliedImage, ...(getResolvedImages(itemQuery) ?? [])].filter(
      (image): image is ResolvedPlaceImage => image !== null,
    );
    const selectedImage =
      candidates.find((image) => {
        const imageId = image.imageUrl || image.sourceUrl;
        return imageId.length > 0 && !usedHotelImageIds.has(imageId);
      }) ?? null;
    if (selectedImage) {
      usedHotelImageIds.add(selectedImage.imageUrl || selectedImage.sourceUrl);
    }
    hotelImageSelections.set(`${item.name}-${index}`, selectedImage);
  });

  const usedDestinationImageIds = new Set<string>();

  return (
    <>
      <div
        className="destination-shortlist flex w-full flex-col gap-6 bg-travel-shortlist-background text-travel-shortlist-foreground"
        data-wayfarer-step="3"
        data-wayfarer-step-available-through="3"
      >
        <div>
          <h1 className="max-w-none text-balance text-3xl font-bold leading-[1.14] tracking-tighter text-travel-shortlist-foreground">
            {title}
          </h1>
          {intro ? (
            <p className="mt-3 max-w-none text-base leading-relaxed text-travel-shortlist-muted">
              {intro}
            </p>
          ) : null}
        </div>

        {places.length === 0 ? (
          <p className="rounded-[15px] border border-travel-shortlist-border bg-travel-shortlist-card px-5 py-6 text-sm text-travel-shortlist-muted">
            {intl.formatMessage(messages.empty)}
          </p>
        ) : (
          <ol className="flex w-full flex-col gap-3.5">
            {places.map((place, index) => {
              const placeQuery = `${place.name} ${place.city} ${place.country}`;
              const suppliedPlaceImage = place.imageUrl
                ? {
                    imageUrl: place.imageUrl,
                    sourceUrl: place.imageSourceUrl,
                    title: place.imageAlt || place.name,
                    credit: place.imageCredit,
                    license: place.imageLicense,
                    matchType: 'exact' as const,
                  }
                : null;
              const placeImageCandidates = [
                ...(getResolvedImages(placeQuery) ?? []),
                ...(suppliedPlaceImage ? [suppliedPlaceImage] : []),
              ];
              const placeImage =
                placeImageCandidates.find((image) => {
                  const imageId = image.imageUrl || image.sourceUrl;
                  return imageId.length > 0 && !usedDestinationImageIds.has(imageId);
                }) ?? placeImageCandidates[0] ?? null;
              if (placeImage) {
                usedDestinationImageIds.add(placeImage.imageUrl || placeImage.sourceUrl);
              }
              const placeImageAlt =
                place.imageAlt ||
                (placeImage?.matchType === 'nearby'
                  ? `Nearby real place photograph near ${place.name}`
                  : placeImage?.matchType === 'regional'
                    ? `Regional real place photograph from ${place.country}`
                    : `${place.name} destination photograph`);
              const cafeSummary = [
                place.cafeOptions === undefined ? '' : `${intl.formatNumber(place.cafeOptions)}+`,
                place.cafePriceRange,
              ]
                .filter((value) => value.length > 0)
                .join(' · ');
              const hasPlanningSnapshot =
                place.bestWindow.length > 0 || cafeSummary.length > 0 || place.thingsToDo.length > 0;
              return (
                <li
                  key={place.id}
                  className="animate-fadeUp motion-reduce:animate-none w-full overflow-hidden rounded-[15px] border border-travel-shortlist-border bg-travel-shortlist-card text-travel-shortlist-foreground"
                  style={{ animationDelay: `${index * 70}ms` }}
                >
                  <div className="grid sm:grid-cols-[14rem_minmax(0,1fr)]">
                    <RemotePlaceImage
                      imageUrl={placeImage?.imageUrl || ''}
                      imageAlt={placeImageAlt}
                      placeholderSeed={placeQuery}
                      onImageError={() => requestFallbackImage(placeQuery)}
                      className="min-h-44 self-stretch sm:min-h-full"
                      loading={index < 2 ? 'eager' : 'lazy'}
                      pending={!place.imageUrl && getResolvedImages(placeQuery) === undefined}
                    />

                    <div className="relative min-w-0 p-5 sm:p-6">
                      <span
                        className="absolute right-5 top-5 flex h-10 w-10 items-center justify-center rounded-full bg-travel-shortlist-background text-sm font-semibold tabular-nums text-travel-shortlist-muted shadow-sm sm:right-6 sm:top-6"
                        aria-hidden="true"
                      >
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <div className="flex items-start justify-between gap-4 pr-20">
                        <div className="min-w-0">
                          <h2 className="flex min-w-0 flex-wrap items-baseline gap-x-2 text-xl font-bold tracking-tight text-travel-shortlist-foreground">
                            <span translate="no">{place.name}</span>
                            {place.country ? (
                              <span
                                className="flex items-center gap-1 text-base font-medium text-travel-shortlist-muted"
                                translate="no"
                              >
                                <MapPin
                                  className="h-4 w-4 shrink-0 text-travel-shortlist-muted"
                                  aria-hidden="true"
                                />
                                {place.country}
                              </span>
                            ) : null}
                          </h2>
                        </div>
                      </div>

                      {place.pitch ? (
                        <p className="mt-2.5 w-full text-base leading-relaxed text-travel-shortlist-foreground">
                          {place.pitch}
                        </p>
                      ) : null}

                      {place.tags.length > 0 ? (
                        <ul className="mt-4 flex flex-wrap gap-1.5">
                          {place.tags.map((tag) => (
                            <li
                              key={tag}
                              className="rounded-full border border-travel-shortlist-border px-2.5 py-1 text-xs font-medium text-travel-shortlist-muted"
                            >
                              {tag}
                            </li>
                          ))}
                        </ul>
                      ) : null}


                      {hasPlanningSnapshot ? (
                        <section className="mt-4" aria-label={intl.formatMessage(messages.snapshot)}>
                          <dl className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm">
                            {place.bestWindow ? (
                              <div className="flex min-w-0 items-center gap-1.5">
                                <dt className="flex shrink-0 items-center gap-1 text-sm font-semibold leading-tight text-travel-shortlist-foreground">
                                  <CalendarDays className="h-4 w-4 shrink-0" aria-hidden="true" />
                                  {intl.formatMessage(messages.window)}
                                </dt>
                                <dd className="min-w-0 text-travel-shortlist-muted" title={place.bestWindow}>
                                  {place.bestWindow}
                                </dd>
                              </div>
                            ) : null}
                            {cafeSummary ? (
                              <div className="flex min-w-0 items-center gap-1.5">
                                <dt className="flex shrink-0 items-center gap-1 text-sm font-semibold leading-tight text-travel-shortlist-foreground">
                                  <Coffee className="h-4 w-4 shrink-0" aria-hidden="true" />
                                  {intl.formatMessage(messages.cafesNearby)}
                                </dt>
                                <dd className="min-w-0 truncate leading-tight text-travel-shortlist-muted" title={cafeSummary}>
                                  {cafeSummary}
                                </dd>
                              </div>
                            ) : null}
                            {place.thingsToDo ? (
                              <div className="flex min-w-0 max-w-full items-center gap-1.5">
                                <dt className="flex shrink-0 items-center gap-1 text-sm font-semibold leading-tight text-travel-shortlist-foreground">
                                  <Sparkles className="h-4 w-4 shrink-0" aria-hidden="true" />
                                  {intl.formatMessage(messages.thingsToDo)}
                                </dt>
                                <dd className="min-w-0 truncate leading-tight text-travel-shortlist-muted" title={place.thingsToDo}>
                                  {place.thingsToDo}
                                </dd>
                              </div>
                            ) : null}
                          </dl>
                        </section>
                      ) : null}

                      {place.watchOut ? (
                        <p className="mt-4 border-t border-travel-shortlist-border pt-3 text-sm text-travel-shortlist-muted">
                          <span className="font-semibold text-travel-shortlist-foreground">
                            {intl.formatMessage(messages.tip)}:{' '}
                          </span>
                          {place.watchOut}
                        </p>
                      ) : null}

                      <div className="mt-5 flex flex-wrap justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setOpenPlaceId(place.id);
                            requestGuide(place);
                          }}
                          aria-expanded={openPlaceId === place.id}
                          className="rounded-input bg-travel-shortlist-foreground px-4 py-2.5 text-sm font-bold text-travel-shortlist-card transition duration-150 [touch-action:manipulation] hover:bg-travel-shortlist-foreground/90 active:scale-[0.99] motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary"
                        >
                          {intl.formatMessage(messages.showPlaces)}
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            dispatch?.('choosePlace', { destinationId: place.id, name: place.name })
                          }
                          className="rounded-input bg-primary px-4 py-2.5 text-sm font-bold text-travel-shortlist-card transition duration-150 [touch-action:manipulation] hover:bg-primary/90 active:scale-[0.99] motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary"
                        >
                          {intl.formatMessage(messages.plan)}
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        )}

        <div className="flex flex-wrap justify-end gap-2 pt-4">
          {places.length > 0 ? (
            <>
              <button
                type="button"
                onClick={() => {
                  appendNextResponseRef.current = true;
                  dispatch?.('loadMore', {
                    currentCount: places.length,
                    shownIds: places.map((place) => place.id),
                  });
                }}
                className="flex shrink-0 items-center gap-2 rounded-input border border-travel-shortlist-border bg-travel-shortlist-card px-3.5 py-2 text-sm font-medium text-travel-shortlist-foreground transition-colors duration-150 [touch-action:manipulation] hover:border-primary hover:text-primary motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary"
              >
                <Plus
                  className="h-4 w-4 shrink-0 text-travel-shortlist-foreground"
                  aria-hidden="true"
                />
                {intl.formatMessage(messages.loadMore)}
              </button>
              <button
                type="button"
                onClick={() => {
                  appendNextResponseRef.current = false;
                  dispatch?.('refineShortlist', { reason: 'none-fit' });
                }}
                className="flex shrink-0 items-center gap-2 rounded-input border border-travel-shortlist-border bg-travel-shortlist-card px-3.5 py-2 text-sm font-medium text-travel-shortlist-foreground transition-colors duration-150 [touch-action:manipulation] hover:border-primary hover:text-primary motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary"
              >
                <RotateCcw
                  className="h-4 w-4 shrink-0 text-travel-shortlist-foreground"
                  aria-hidden="true"
                />
                {intl.formatMessage(messages.refine)}
              </button>
            </>
          ) : null}
        </div>
      </div>

      {selectedPlace
        ? createPortal(
            <div
              className="fixed inset-0 z-[100] flex items-center justify-center bg-travel-shortlist-foreground/55 p-4"
              role="presentation"
              onMouseDown={() => setOpenPlaceId(null)}
            >
              <div
                className="flex max-h-[calc(100vh-2rem)] w-full max-w-[1240px] flex-col overflow-hidden rounded-[15px] border border-travel-shortlist-border bg-travel-shortlist-card text-travel-shortlist-foreground tracking-[0.3px] shadow-2xl"
                role="dialog"
                aria-modal="true"
                data-wayfarer-place-guide="true"
                aria-labelledby={`destination-guide-${selectedPlace.id}`}
                onMouseDown={(event) => event.stopPropagation()}
              >
                <div className="flex items-start justify-between gap-4 px-4 pb-0 pt-4">
                  <div className="min-w-0">
                    <h2
                      id={`destination-guide-${selectedPlace.id}`}
                      className="text-2xl font-bold tracking-[0.3px] text-travel-shortlist-foreground"
                    >
                      {intl.formatMessage(messages.guideHeading, { city: selectedCity })}
                    </h2>
                  </div>
                  <button
                    type="button"
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-travel-shortlist-border bg-travel-shortlist-card text-travel-shortlist-foreground transition-colors hover:border-travel-shortlist-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary"
                    aria-label={intl.formatMessage(messages.closeGuide)}
                    onClick={() => setOpenPlaceId(null)}
                  >
                    <X className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto p-4">
                  {hasGuideItems || isSelectedGuideLoading ? (
                    // The map is mounted the moment the popup opens — before the
                    // guide text exists — so the town is on screen while the
                    // named places are still being written. It stays the same
                    // map instance when they land: rebuilding it here would
                    // throw away the framed city and reload every tile.
                    <div className="space-y-4">
                      {isSelectedGuideLoading ? renderGuideLoadingBanner() : null}
                      <div className="grid min-w-0 gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
                        <div className="relative min-w-0">
                          <OpenStreetMapGuideMap
                            city={selectedCity}
                            country={selectedPlace.country}
                            markers={mapMarkers}
                            onMarkerSelect={(sectionKey, name) =>
                              setSelectedGuideItem({
                                sectionKey: sectionKey as GuideSectionKey,
                                name,
                              })
                            }
                            ariaLabel={intl.formatMessage(messages.mapLabel)}
                          />
                          <div className="pointer-events-none absolute inset-x-4 top-4 z-[600] flex flex-wrap justify-end gap-2">
                            {[hotelSection, ...nearbySections]
                              .filter((section) => section && section.items.length > 0)
                              .map((section) => {
                                if (!section) {
                                  return null;
                                }
                                return (
                                  <span
                                    key={section.key}
                                    className="flex items-center gap-1.5 rounded-full border border-travel-shortlist-border bg-travel-shortlist-card/95 px-2.5 py-1 text-xs font-semibold text-travel-shortlist-muted shadow-sm"
                                  >
                                    <span
                                      className={`h-2.5 w-2.5 rounded-full ${section.markerClass}`}
                                      aria-hidden="true"
                                    />
                                    {intl.formatMessage(section.label)}
                                  </span>
                                );
                              })}
                          </div>
                        </div>

                        {hasGuideItems ? renderNearbyGuideCards() : renderGuideSectionSkeletons()}
                      </div>

                      {topFivePicks.length > 0 ? (
                        <section>
                          <div className="flex flex-wrap items-end justify-between gap-3">
                            <h3 className="text-lg font-bold tracking-[0.3px] text-travel-shortlist-foreground">
                              {intl.formatMessage(messages.nearbyPicks)}
                            </h3>
                          </div>
                          <div className="mt-4 grid items-stretch gap-4 sm:grid-cols-2 lg:grid-cols-5">
                            {topFivePicks.map((pick, index) => {
                              const item = pick.item;
                              const PickIcon = pick.Icon;
                              const itemQuery = `${item.name} ${item.area} hotel exterior ${selectedPlace.name} ${selectedPlace.country}`;
                              const itemImage = hotelImageSelections.get(`${item.name}-${index}`) ?? null;
                              const itemImageAlt =
                                item.imageAlt ||
                                (itemImage?.matchType === 'nearby'
                                  ? 'Nearby real hotel photograph near ' + item.name
                                  : itemImage?.matchType === 'regional'
                                    ? 'Regional hotel photograph from ' + selectedPlace.country
                                    : item.name + ' hotel photograph');
                              return (
                                <article
                                  key={'top-pick-' + item.name + '-' + index}
                                  className={`flex h-full min-w-0 flex-col overflow-hidden rounded-[15px] border bg-travel-shortlist-card transition-[border-color,box-shadow] duration-150 motion-reduce:transition-none ${
                                    selectedGuideItem?.sectionKey === 'hotels' &&
                                    normalizeImageQuery(selectedGuideItem.name) === normalizeImageQuery(item.name)
                                      ? 'border-primary ring-2 ring-primary/20'
                                      : 'border-travel-shortlist-border'
                                  }`}
                                >
                                  <div className="relative">
                                    <RemotePlaceImage
                                      imageUrl={itemImage?.imageUrl || ''}
                                      imageAlt={itemImageAlt}
                                      placeholderKind="hotel"
                                      placeholderIndex={index}
                                      placeholderSeed={selectedPlace.id + '-' + item.name}
                                      onImageError={() => requestFallbackImage(itemQuery)}
                                      className="aspect-[4/3] w-full"
                                      pending={!itemImage && getResolvedImages(itemQuery) === undefined}
                                    />
                                    <span
                                      className={`absolute left-2 top-2 flex h-7 w-7 items-center justify-center rounded-full text-travel-shortlist-card shadow-sm ${pick.markerClass}`}
                                    >
                                      <PickIcon className="h-3.5 w-3.5" aria-hidden="true" />
                                    </span>
                                  </div>
                                  <div className="flex flex-1 flex-col p-3">
                                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                                      <h4 className="break-words text-sm font-bold leading-snug text-travel-shortlist-foreground">
                                        {item.name}
                                      </h4>
                                      {selectedGuideItem?.sectionKey === 'hotels' &&
                                      normalizeImageQuery(selectedGuideItem.name) === normalizeImageQuery(item.name) ? (
                                        <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em] text-primary">
                                          {intl.formatMessage(messages.selectedOnMap)}
                                        </span>
                                      ) : null}
                                    </div>
                                    {item.detail ? (
                                      <p className="mt-2 whitespace-normal break-words text-xs leading-relaxed text-travel-shortlist-muted">
                                        {item.detail}
                                      </p>
                                    ) : null}
                                    {item.priceTag ? (
                                      <span className="mt-auto inline-flex max-w-full self-start whitespace-normal break-words rounded-full border border-travel-shortlist-border bg-travel-shortlist-card px-2.5 py-0.5 text-xs font-semibold leading-snug text-travel-shortlist-foreground">
                                        {displayGuidePrice(item.priceTag)}
                                      </span>
                                    ) : null}
                                  </div>
                                </article>
                              );
                            })}
                          </div>
                        </section>
                      ) : isSelectedGuideLoading ? (
                        renderGuidePickSkeletons()
                      ) : null}
                    </div>
                  ) : failedGuideId === selectedPlace.id ? (
                    <div
                      className="flex flex-wrap items-center justify-between gap-3 rounded-[15px] border border-travel-shortlist-border bg-travel-shortlist-background px-4 py-5"
                      aria-live="polite"
                    >
                      <p className="min-w-0 text-sm leading-relaxed text-travel-shortlist-muted">
                        {intl.formatMessage(messages.guideLoadFailed, { city: selectedCity })}
                      </p>
                      <button
                        type="button"
                        onClick={() => sendGuideRequest(selectedPlace)}
                        className="rounded-input bg-travel-shortlist-foreground px-4 py-2.5 text-sm font-bold text-travel-shortlist-card transition duration-150 [touch-action:manipulation] hover:bg-travel-shortlist-foreground/90 active:scale-[0.99] motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary"
                      >
                        {intl.formatMessage(messages.retryGuide)}
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-wrap items-center justify-between gap-3 rounded-[15px] border border-travel-shortlist-border bg-travel-shortlist-background px-4 py-5">
                      <p className="min-w-0 text-sm leading-relaxed text-travel-shortlist-muted">
                        {intl.formatMessage(messages.emptyGuide)}
                      </p>
                      <button
                        type="button"
                        onClick={() => sendGuideRequest(selectedPlace)}
                        className="rounded-input bg-travel-shortlist-foreground px-4 py-2.5 text-sm font-bold text-travel-shortlist-card transition duration-150 [touch-action:manipulation] hover:bg-travel-shortlist-foreground/90 active:scale-[0.99] motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary"
                      >
                        {intl.formatMessage(messages.retryGuide)}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
};
